"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const BackgroundBeamsWithCollision = ({ children }) => {
  const parentRef = useRef(null);
  const containerRef = useRef(null);

  // Estado para pegar largura e altura da tela
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Atualiza o tamanho ao redimensionar
  useEffect(() => {
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Quantidade de raios baseada na largura da tela (um raio a cada 150px por exemplo)
  const numBeams = Math.ceil(windowSize.width / 150);

  // Gera os raios com posições X espaçadas igualmente
  const beams = Array.from({ length: numBeams }).map((_, i) => {
  const spacing = windowSize.width / (numBeams - 1); // Divide por (numBeams - 1) pra preencher até o final
  const baseX = spacing * i;

  const randomOffsetX = Math.random() * 30 - 15; // Mantém variação, mas menor pra não criar buracos
  const randomDelay = Math.random() * 2;          // Delay aleatório entre 0 ~ 2 segundos
  const randomDuration = 4 + Math.random() * 3;   // Duração entre 4 ~ 7 segundos

  return {
    initialX: baseX + randomOffsetX,
    translateX: baseX + randomOffsetX,  
    duration: randomDuration,
    repeatDelay: Math.random() * 1,                // Pequeno delay entre loops
    delay: randomDelay,
    className: Math.random() > 0.5 ? "h-20" : "h-10",
  };
});


  return (
    <div
      ref={parentRef}
      style={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        backgroundImage: "url('/assets/sequence_gradient.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
      }}
    >
      {beams.map((beam, idx) => (
        <CollisionMechanism
          key={"beam" + idx}
          beamOptions={beam}
          containerRef={containerRef}
          parentRef={parentRef}
        />
      ))}
      {children}
      <div
        ref={containerRef}
        style={{
          position: "absolute",
          bottom: 0,
          width: "100%",
          height: 5,
          pointerEvents: "none",
          boxShadow:
            "0 0 24px rgba(34,42,53,0.06),0 1px 1px rgba(0,0,0,0.05),0 0 0 1px rgba(34,42,53,0.04),0 0 4px rgba(34,42,53,0.08),0 16px 68px rgba(47,48,55,0.05),0 1px 0 rgba(255,255,255,0.1) inset",
        }}
      />
    </div>
  );
};

const CollisionMechanism = React.forwardRef(({ parentRef, containerRef, beamOptions = {} }, ref) => {
  const beamRef = useRef(null);
  const [collision, setCollision] = useState({ detected: false, coordinates: null });
  const [beamKey, setBeamKey] = useState(0);
  const [cycleCollisionDetected, setCycleCollisionDetected] = useState(false);

  useEffect(() => {
    const checkCollision = () => {
      if (beamRef.current && containerRef.current && parentRef.current && !cycleCollisionDetected) {
        const beamRect = beamRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const parentRect = parentRef.current.getBoundingClientRect();

        if (beamRect.bottom >= containerRect.top) {
          const relativeX = beamRect.left - parentRect.left + beamRect.width / 2;
          const relativeY = beamRect.bottom - parentRect.top;

          setCollision({ detected: true, coordinates: { x: relativeX, y: relativeY } });
          setCycleCollisionDetected(true);
        }
      }
    };

    const interval = setInterval(checkCollision, 50);
    return () => clearInterval(interval);
  }, [cycleCollisionDetected, containerRef, parentRef]);

  useEffect(() => {
  if (collision.detected && collision.coordinates) {
    const t1 = setTimeout(() => {
      setCollision({ detected: false, coordinates: null });
      setCycleCollisionDetected(false);
    }, 2000);

    return () => clearTimeout(t1);
  }
}, [collision]);


  return (
    <>
      <motion.div
        key={beamKey}
        ref={beamRef}
        animate="animate"
        initial={{
          translateY: "-20px",
          translateX: beamOptions.initialX,
          rotate: 0,
        }}
        variants={{
          animate: {
            translateY: "110vh",
            translateX: beamOptions.translateX,
            rotate: 0,
          },
        }}
        transition={{
          duration: beamOptions.duration || 8,
          repeat: Infinity,
          repeatType: "loop",
          ease: "linear",
          delay: beamOptions.delay || 0,
          repeatDelay: beamOptions.repeatDelay || 0,
        }}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "2px",
          borderRadius: "9999px",
          background: "linear-gradient(to top, #5a189a, #7b2ff7, transparent)",
          height: beamOptions.className === "h-20" ? 80 : 40,
        }}
      />
      <AnimatePresence>
        {collision.detected && collision.coordinates && (
          <Explosion
            key={`${collision.coordinates.x}-${collision.coordinates.y}`}
            style={{
              position: "absolute",
              left: collision.coordinates.x,
              top: collision.coordinates.y,
              transform: "translate(-50%, -50%)",
              width: 20,
              height: 20,
              pointerEvents: "none",
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
});

CollisionMechanism.displayName = "CollisionMechanism";

const Explosion = (props) => {
  const particles = Array.from({ length: 20 }).map((_, i) => ({
    id: i,
    xDir: Math.random() * 80 - 40,
    yDir: Math.random() * -50 - 10,
  }));

  return (
    <div {...props} className="absolute z-50">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        className="absolute top-0 left-1/2 w-10 h-2 rounded-full bg-gradient-to-r from-transparent via-purple-600 to-transparent blur-sm -translate-x-1/2"
      />
      {particles.map(({ id, xDir, yDir }) => (
        <motion.span
          key={id}
          initial={{ x: 0, y: 0, opacity: 1 }}
          animate={{ x: xDir, y: yDir, opacity: 0 }}
          transition={{ duration: Math.random() * 1.5 + 0.5, ease: "easeOut" }}
          className="absolute h-1 w-1 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500"
        />
      ))}
    </div>
  );
};

export default BackgroundBeamsWithCollision;
